/**
 * Command Center (Dashboard) — multi-tab panely jako My Panel, oddělená perzistence.
 * Default = ops layout z DASHBOARD_DEFAULT_LAYOUT.
 */
import i18n from '../lib/i18n';
import { mcpCall } from '../lib/mcp';
import { DASHBOARD_DEFAULT_LAYOUT } from './registry.dashboard';
import type { WidgetInstance, WidgetPanel } from './types';
import type { PanelsState } from './panels-store';

const LS_KEY = 'acm_command_center_v1';
const PREFS_KEY = 'command_center';

function mk(id: string, label: string, widgets: WidgetInstance[]): WidgetPanel {
  return { id, label, node: null, widgets };
}

export const DEFAULT_COMMAND_CENTER: PanelsState = {
  activeId: 'ops',
  panels: [
    mk('ops', i18n.t('dashboard.tabOps', 'Operace'), [...DASHBOARD_DEFAULT_LAYOUT] as WidgetInstance[]),
    mk('fleet', i18n.t('dashboard.tabFleet', 'Flotila'), [
      { widgetId: 'dashboard.mesh-nodes', format: 'lg' },
      { widgetId: 'dashboard.stat-mesh', format: 'sm' },
      { widgetId: 'dashboard.stat-tenants', format: 'sm' },
      { widgetId: 'dashboard.tenants', format: 'md' },
      { widgetId: 'dashboard.acm-services', format: 'md' },
    ]),
    mk('health', i18n.t('dashboard.tabHealth', 'Zdraví'), [
      { widgetId: 'dashboard.stat-health', format: 'sm' },
      { widgetId: 'dashboard.health-checks', format: 'lg' },
      { widgetId: 'dashboard.core-containers', format: 'md' },
      { widgetId: 'dashboard.tasks', format: 'md' },
      { widgetId: 'dashboard.audit', format: 'md' },
    ]),
  ],
};

export function loadCommandCenter(): PanelsState {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return structuredClone(DEFAULT_COMMAND_CENTER);
    const parsed = JSON.parse(raw) as PanelsState;
    if (!parsed.panels?.length) return structuredClone(DEFAULT_COMMAND_CENTER);
    if (!parsed.panels.some((p) => p.id === parsed.activeId)) {
      parsed.activeId = parsed.panels[0]!.id;
    }
    return parsed;
  } catch {
    return structuredClone(DEFAULT_COMMAND_CENTER);
  }
}

function valid(s: unknown): PanelsState | null {
  const p = s as PanelsState | null;
  if (!p || !Array.isArray(p.panels) || !p.panels.length) return null;
  if (!p.panels.some((x) => x.id === p.activeId)) p.activeId = p.panels[0]!.id;
  return p;
}

let saveTimer: ReturnType<typeof setTimeout> | null = null;

export function saveCommandCenter(state: PanelsState): void {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(state));
  } catch { /* */ }
  if (saveTimer) clearTimeout(saveTimer);
  saveTimer = setTimeout(() => {
    mcpCall('ui.prefs_set', { key: PREFS_KEY, value: state }).catch(() => {});
  }, 800);
}

export async function hydrateCommandCenter(): Promise<PanelsState | null> {
  try {
    const r = await mcpCall<{ value?: PanelsState | null }>('ui.prefs_get', { key: PREFS_KEY });
    return valid(r?.value ?? null);
  } catch {
    return null;
  }
}

export function newCcPanelId(existing: WidgetPanel[]): string {
  let n = existing.length + 1;
  while (existing.some((p) => p.id === `cc-${n}`)) n++;
  return `cc-${n}`;
}
